import type {
  CustomerFeatureListItem,
  CustomerFeatureValue,
  FeatureRegion,
} from "@wallyo/contracts/console/configuration";
import { FEATURE_REGIONS } from "@wallyo/contracts/console/configuration";
import { Fragment, type FormEvent, type ReactNode } from "react";
import { categoryName, customerFeatureName, t } from "../../i18n";
import { formatDateTime } from "../../i18n/format";
import { configHref } from "../config-route";
import { customerHref } from "../customers/customer-route";
import { WriteFailureNotice } from "../team/TeamDialogs";
import type { WriteFailure } from "../team/team-format";
import {
  audienceOf,
  groupByCategory,
  regionLabel,
  scopeChoices,
  scopeLabel,
  targetLabel,
  targetUserId,
  valueLabel,
  type Audience,
  type ExceptionFields,
} from "./features-model";

const AUDIENCE = {
  clients: "features.for.clients",
  pros: "features.for.pros",
  everyone: "features.for.everyone",
} as const satisfies Record<Audience, string>;

export function Sensitivity({ level }: { level: CustomerFeatureListItem["sensitivity"] }) {
  return <span className={`sens ${level}`}>{t(`team.sensitivity.${level}`)}</span>;
}

export function ValuePill({ value }: { value: boolean }) {
  return <span className={`pill ${value ? "ok" : "bad"}`}>{valueLabel(value)}</span>;
}

// ── The list ─────────────────────────────────────────────────────────────

/** Every customer feature by category: who it is for, its default, and how many exceptions it has. */
export function FeatureList({
  features,
  selectedId,
}: {
  features: readonly CustomerFeatureListItem[];
  selectedId: string | null;
}) {
  return (
    <div className="card">
      <div className="table-wrap">
        <table className="config-list">
          <thead>
            <tr>
              <th>{t("features.col.feature")}</th>
              <th>{t("features.col.default")}</th>
              <th className="num">{t("features.col.exceptions")}</th>
            </tr>
          </thead>
          <tbody>
            {groupByCategory(features).map((group) => (
              <Fragment key={group.category}>
                <tr className="cat">
                  <td colSpan={3}>{categoryName(group.category)}</td>
                </tr>
                {group.features.map((feature) => (
                  <tr
                    key={feature.settingId}
                    className={feature.settingId === selectedId ? "sel" : undefined}
                    data-feature={feature.settingId}
                  >
                    <td>
                      <a
                        className="row-link"
                        href={configHref("features", feature.settingId)}
                        aria-current={feature.settingId === selectedId ? "true" : undefined}
                      >
                        {customerFeatureName(feature.settingId)}
                      </a>
                      <Sensitivity level={feature.sensitivity} />
                      {feature.status === "planned" ? (
                        <>
                          {" "}
                          <span className="pill mute">{t("features.planned")}</span>
                        </>
                      ) : null}
                      <br />
                      <span className="meta-id" data-audience={audienceOf(feature)}>
                        {t(AUDIENCE[audienceOf(feature)])}
                      </span>
                      <br />
                      <span className="mono meta-id">{feature.settingId}</span>
                    </td>
                    <td>
                      <span className={`pill ${feature.defaultValue ? "ok" : "mute"}`}>
                        {valueLabel(feature.defaultValue)}
                      </span>
                    </td>
                    <td className="num">{feature.exceptionCount}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── One feature ──────────────────────────────────────────────────────────

export type FeatureDetailProps = {
  feature: CustomerFeatureListItem;
  values: readonly CustomerFeatureValue[];
  /** Links a person to their customer page (VIEW_ACCOUNTS). */
  canViewAccounts: boolean;
  /** Why this person can't change it, or null. */
  lock: "critical" | null;
  roles: readonly string[];
  /** The value being edited, if any: its row is highlighted. */
  editingId: string | null;
  onAdd: () => void;
  onEdit: (value: CustomerFeatureValue) => void;
  onRemove: (value: CustomerFeatureValue) => void;
  /** The add or edit form, when open. */
  editor?: ReactNode;
};

/** A feature's sensitivity in words: who may change it, and why it is locked for this person. */
function SensitivityNote({ feature, lock, roles }: Pick<FeatureDetailProps, "feature" | "lock" | "roles">) {
  return (
    <>
      <p className="foot">
        {feature.sensitivity === "critical"
          ? t("features.sensitivity.critical")
          : t("features.sensitivity.open", { level: t(`team.sensitivity.${feature.sensitivity}`) })}
      </p>
      {lock === "critical" ? (
        <div className="denied config-lock" role="status">
          🔒 {t("features.locked.critical", { roles: roles.join(", ") })}
        </div>
      ) : null}
    </>
  );
}

export function FeatureDetail(props: FeatureDetailProps) {
  const { feature, values, lock } = props;
  return (
    <div className="card" data-detail={feature.settingId}>
      <div>
        <h3>
          {customerFeatureName(feature.settingId)}
          <Sensitivity level={feature.sensitivity} />
        </h3>
        <p className="sub mono">{feature.settingId}</p>
      </div>
      <p className="foot">{t("features.detail.default", { value: valueLabel(feature.defaultValue) })}</p>
      {feature.status === "planned" ? (
        <p className="meta">
          <span className="pill mute">{t("features.planned")}</span> {t("features.planned.note")}
        </p>
      ) : null}
      <SensitivityNote feature={feature} lock={lock} roles={props.roles} />
      <h2>{t("features.exceptions.title")}</h2>
      <div className="table-wrap">
        <table className="config-values">
          <thead>
            <tr>
              <th>{t("features.col.scope")}</th>
              <th>{t("features.col.target")}</th>
              <th>{t("features.col.value")}</th>
              <th>{t("features.col.reason")}</th>
              <th>{t("features.col.expires")}</th>
              <th>
                <span className="visually-hidden">{t("features.col.actions")}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="default-row">
              <td>{t("features.default.scope")}</td>
              <td>{t("features.target.everyone")}</td>
              <td>
                <ValuePill value={feature.defaultValue} />
              </td>
              <td className="muted-cell">{t("features.default.reason")}</td>
              <td>—</td>
              <td />
            </tr>
            {values.map((value) => (
              <ValueRow key={value.settingValueId} {...props} value={value} />
            ))}
          </tbody>
        </table>
      </div>
      {values.length === 0 ? <p className="foot">{t("features.exceptions.none")}</p> : null}
      {props.editor ??
        (lock ? null : (
          <div className="btns">
            <button className="btn primary" type="button" onClick={props.onAdd}>
              {t("features.add")}
            </button>
          </div>
        ))}
    </div>
  );
}

function ValueRow(props: FeatureDetailProps & { value: CustomerFeatureValue }) {
  const { value } = props;
  const userId = targetUserId(value.target);
  const name = targetLabel(value);
  const classes = [value.expired ? "expired" : "", props.editingId === value.settingValueId ? "sel" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <tr className={classes || undefined} data-value={value.settingValueId}>
      <td>{scopeLabel(value.target.scope)}</td>
      <td>
        {userId && props.canViewAccounts ? (
          <a className="subject-link" href={customerHref(userId)}>
            <bdi>{name}</bdi>
          </a>
        ) : (
          <bdi>{name}</bdi>
        )}
      </td>
      <td>
        <ValuePill value={value.value} />
      </td>
      <td>{value.reason ? <bdi>{value.reason}</bdi> : <span className="muted-cell">—</span>}</td>
      <td>
        {value.expiresAt ? formatDateTime(value.expiresAt) : t("features.expires.never")}
        {value.expired ? (
          <>
            {" "}
            <span className="pill warn">{t("features.expired")}</span>
          </>
        ) : null}
      </td>
      <td className="row-actions">
        {props.lock ? null : (
          <>
            <button className="btn quiet small" type="button" onClick={() => props.onEdit(value)}>
              {t("features.edit")}
            </button>
            <button className="btn quiet small" type="button" onClick={() => props.onRemove(value)}>
              {t("features.remove")}
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

// ── Add or edit an exception ─────────────────────────────────────────────

export type ExceptionFormViewProps = {
  feature: CustomerFeatureListItem;
  /** The exception being changed, or null for a new one. */
  editing: CustomerFeatureValue | null;
  fields: ExceptionFields;
  onChange: (change: Partial<ExceptionFields>) => void;
  canSearch: boolean;
  /** The person or pro picker, shown for those scopes. */
  personPicker?: ReactNode;
  /** The earliest expiry the input offers, as a `datetime-local` value. */
  minExpiry: string;
  pending: boolean;
  problem: string | null;
  failure: WriteFailure | null;
  onSubmit: () => void;
  onCancel: () => void;
  onReload: () => void;
};

export function ExceptionFormView(props: ExceptionFormViewProps) {
  const { feature, fields, editing } = props;
  const choices = scopeChoices(feature, props.canSearch);
  const unsupported = choices.filter((choice) => choice.unavailable === "unsupported");
  const needsSearch = choices.some((choice) => choice.unavailable === "needsSearch");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    props.onSubmit();
  };
  return (
    <form className="config-form" onSubmit={submit} noValidate aria-labelledby="exception-form-title">
      <h2 id="exception-form-title">{t(editing ? "features.form.editTitle" : "features.form.addTitle")}</h2>
      <div className="form">
        <div className="field">
          <label htmlFor="exception-scope">{t("features.form.scope")}</label>
          {editing ? (
            <input
              id="exception-scope"
              value={`${scopeLabel(editing.target.scope)} · ${targetLabel(editing)}`}
              readOnly
            />
          ) : (
            <select
              id="exception-scope"
              value={fields.scope}
              onChange={(event) =>
                props.onChange({ scope: event.target.value as ExceptionFields["scope"], person: null })
              }
            >
              <option value="">{t("features.form.scopePick")}</option>
              {choices.map((choice) => (
                <option key={choice.scope} value={choice.scope} disabled={choice.unavailable !== null}>
                  {choice.unavailable === "unsupported"
                    ? t("features.form.scopeUnsupported", { scope: scopeLabel(choice.scope) })
                    : scopeLabel(choice.scope)}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="field">
          <label htmlFor="exception-value">{t("features.form.value")}</label>
          <select
            id="exception-value"
            value={fields.value ? "on" : "off"}
            onChange={(event) => props.onChange({ value: event.target.value === "on" })}
          >
            <option value="on">{t("features.on")}</option>
            <option value="off">{t("features.off")}</option>
          </select>
        </div>
        {!editing && fields.scope === "region" ? (
          <div className="field">
            <label htmlFor="exception-region">{t("features.form.region")}</label>
            <select
              id="exception-region"
              value={fields.region}
              onChange={(event) => props.onChange({ region: event.target.value as FeatureRegion | "" })}
            >
              <option value="">{t("features.form.regionPick")}</option>
              {FEATURE_REGIONS.map((region) => (
                <option key={region} value={region}>
                  {regionLabel(region)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {!editing && fields.scope === "appVersion" ? (
          <div className="field">
            <label htmlFor="exception-version">{t("features.form.appVersion")}</label>
            <input
              id="exception-version"
              inputMode="decimal"
              placeholder="2.4.0"
              value={fields.appVersion}
              onChange={(event) => props.onChange({ appVersion: event.target.value })}
              dir="ltr"
            />
            <span className="meta">{t("features.form.appVersionHint")}</span>
          </div>
        ) : null}
        <div className="field">
          <label htmlFor="exception-expiry">{t("features.form.expires")}</label>
          <input
            id="exception-expiry"
            type="datetime-local"
            min={props.minExpiry}
            value={fields.expiresAt}
            onChange={(event) => props.onChange({ expiresAt: event.target.value })}
          />
          <span className="meta">{t("features.form.expiresHint")}</span>
        </div>
        <div className="field">
          <label htmlFor="exception-reason">{t("features.form.reason")}</label>
          <input
            id="exception-reason"
            value={fields.reason}
            onChange={(event) => props.onChange({ reason: event.target.value })}
          />
          <span className="meta">{t("features.form.reasonHint")}</span>
        </div>
      </div>
      {!editing && (fields.scope === "user" || fields.scope === "proProfile") ? props.personPicker : null}
      {!editing && unsupported.length > 0 ? <p className="foot">{t("features.form.clientProfileNote")}</p> : null}
      {!editing && needsSearch ? (
        <p className="foot">
          {t("features.form.needsSearch")} <span className="mono">SEARCH_ACCOUNTS_STAFF_FEATURE_ENABLEMENT</span>
        </p>
      ) : null}
      {editing ? <p className="foot">{t("features.form.version", { version: editing.version })}</p> : null}
      {props.problem ? <p className="field-problem">{props.problem}</p> : null}
      {props.failure ? <WriteFailureNotice failure={props.failure} onReload={props.onReload} /> : null}
      <div className="btns">
        <button className="btn quiet" type="button" onClick={props.onCancel}>
          {t("features.form.cancel")}
        </button>
        <button className="btn primary" type="submit" disabled={props.pending}>
          {props.pending
            ? t("features.form.sending")
            : t(editing ? "features.form.submitEdit" : "features.form.submitAdd")}
        </button>
      </div>
    </form>
  );
}
